import { useEffect, useMemo, useState } from 'react';
import { StoreSettings } from '@/types';
import {
    Accordion,
    AccordionSummary,
    Typography,
    AccordionDetails,
    FormControlLabel,
    Checkbox,
    Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

const OfficeAutomationAccordion = ({
    settings,
    handleSettingChange,
}: {
    settings: Partial<StoreSettings>;
    handleSettingChange: (key: keyof StoreSettings, value: StoreSettings[keyof StoreSettings]) => void;
}) => {
    const { t } = useTranslation('common');
    const [officeStart, setOfficeStart] = useState<Dayjs | null>(null);
    const [officeEnd, setOfficeEnd] = useState<Dayjs | null>(null);

    const hasError = useMemo(() => !officeStart || !officeEnd, [officeStart, officeEnd]);

    useEffect(() => {
        const startStr = (settings.ohauto_start_times as string) || '';
        const endStr = (settings.ohauto_end_times as string) || '';

        const startVal = startStr ? dayjs(startStr, 'HH:mm') : null;
        const endVal = endStr ? dayjs(endStr, 'HH:mm') : null;

        setOfficeStart(startVal?.isValid() ? startVal : null);
        setOfficeEnd(endVal?.isValid() ? endVal : null);
    }, [settings.ohauto_start_times, settings.ohauto_end_times]);

    const handleOfficeStartChange = (value: Dayjs | null) => {
        setOfficeStart(value);

        if (!value || !value.isValid()) {
            handleSettingChange('ohauto_start_times', '' as StoreSettings[keyof StoreSettings]);
            return;
        }

        const normalized = value.second(0).millisecond(0);
        handleSettingChange('ohauto_start_times', normalized.format('HH:mm') as StoreSettings[keyof StoreSettings]);
    };

    const handleOfficeEndChange = (value: Dayjs | null) => {
        setOfficeEnd(value);

        if (!value || !value.isValid()) {
            handleSettingChange('ohauto_end_times', '' as StoreSettings[keyof StoreSettings]);
            return;
        }

        const normalized = value.second(0).millisecond(0);
        handleSettingChange('ohauto_end_times', normalized.format('HH:mm') as StoreSettings[keyof StoreSettings]);
    };

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{t('Office Hours Automation')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <Typography variant="body2">
                        <Box component="span" fontWeight={700}>
                            {t(
                                'Office Hours Automation is a feature that allows certain tasks or functions in AURA to automatically run during your designated office hours.',
                            )}
                        </Box>
                    </Typography>
                    <Typography variant="body2">
                        {t('During Office Hours, the following tasks will automatically run:')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        1. {t('Wake-up signal will be sent periodically.')}
                    </Typography>
                    <Typography variant="body2">
                        2. {t('Switch channel from default channel to AP channel.')}
                    </Typography>
                    <Typography variant="body2">
                        3. {t('At the end of office hours, a one-time stop wake-up signal will be sent.')}
                    </Typography>
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={settings.ohauto_enabled === '1'}
                            onChange={(e) => handleSettingChange('ohauto_enabled', e.target.checked ? '1' : '0')}
                        />
                    }
                    label={t('Enable Office Hours Automation')}
                />
                {settings.ohauto_enabled === '1' && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 200 }}>
                                    <Typography variant="subtitle1">{t('Office Hours Schedule')}</Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        maxWidth: 300,
                                    }}
                                >
                                    <TimePicker
                                        value={officeStart}
                                        onChange={handleOfficeStartChange}
                                        ampm={false}
                                        format="HH:mm"
                                        views={['hours', 'minutes']}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                error: !officeStart,
                                            },
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ px: 1 }}>
                                        {t('to')}
                                    </Typography>
                                    <TimePicker
                                        value={officeEnd}
                                        onChange={handleOfficeEndChange}
                                        ampm={false}
                                        format="HH:mm"
                                        views={['hours', 'minutes']}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                error: !officeEnd,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>

                            {hasError && (
                                <Box sx={{ display: 'flex' }}>
                                    <Box sx={{ minWidth: 200 }} />
                                    <Typography variant="caption" color={'error'} sx={{ mt: 0.5 }}>
                                        {t('Please finish both start and end time.')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </LocalizationProvider>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
export default OfficeAutomationAccordion;
